import dayjs from "dayjs";
import type { ServiceFailureReasonCode } from "../../pages/linehaul/ucccOfd/ucccOfdRightPanel/ucccOfdDetails/ucccOfdRightPanelStatusDetails.type";
import type { UcccOfdShipmentInformation } from "../../pages/linehaul/ucccOfd/ucccOfdShipmentInformation.type";
import type {
  UcccOfdDashboardSummaryData,
  UcccOfdShipmentRow,
  UcccOfdShipmentsListResponse,
} from "../../pages/linehaul/ucccOfd/ucccOfdShipmentRow.type";

// TODO(LH-2806): [FE][API] Integrate Table Data and Sorting API — list mock mirrors partial API contract; extend when sorting/pagination responses are defined (see handlers MSW + useUcccOfdShipments).

export const mockUcccOfdShipments = {
  default: {
    totalCount: 473,
    shipments: [
      {
        id: "110930229",
        shipmentId: 110930229,
        proNumber: "134842651",
        pickupDateUtc: dayjs("2024-08-09T07:00:00Z"),
        dueDate: dayjs("2024-08-16"),
        deliveryDateUtc: dayjs("2024-08-16T19:00:00Z"),
        appointmentStartTimestampUtc: dayjs("2024-08-17T19:00:00Z"),
        originSic: {
          id: 1,
          code: "EWR",
          ownership: "C",
          lhRegionId: 5,
          timeZone: "America/New_York",
        },
        destinationSic: {
          id: 2,
          code: "ATL",
          ownership: "C",
          lhRegionId: 3,
          timeZone: "America/New_York",
        },
        currentSic: {
          id: 2,
          code: "ATL",
          ownership: "C",
          lhRegionId: 3,
          timeZone: "America/New_York",
        },
        finalDestination: "ATL",
        pts: 1,
        pcs: 2,
        weight: 1452,
        shipmentTags: ["CC", "UC", "APTM"],
        status: "ENRT",
        tripNumber: 756394,
        etaUtc: dayjs("2024-08-16T19:00:00Z"),
        ataUtc: dayjs("2024-08-16T18:50:00Z"),
        etaStatus: "ON TIME",
        locationDetails: {
          state: "TN",
          city: "Mount Pleasant",
          zipCode: "38647",
          locationSic: "ATL",
        },
        consignee: {
          id: 87520,
          name: "CAPE MARINE INC",
          address: "1427 N Main ST",
          city: "Mount Pleasant",
          state: "TN",
          zipCode: "38647",
        },
        noDimensions: false,
        serviceFailure: false,
        expediteShipment: true,
        hasProWarning: true,
        arrivalToSic: "8/16/24 1500",
        arrivalToSicStatus: "ON TIME",
        appt: "8/17/24 1500",
        fbFollowUpStatus: "PENDING APPT",
        currentZone: {
          id: 2,
          code: "ATL",
          ownership: "C",
          lhRegionId: 3,
          timeZone: "New Jersey",
        },
      },
      {
        id: "110930230",
        shipmentId: 110930230,
        proNumber: "110930230",
        pickupDateUtc: dayjs("2024-08-09T07:00:00Z"),
        dueDate: dayjs("2024-08-16"),
        deliveryDateUtc: dayjs("2024-08-16T19:00:00Z"),
        appointmentStartTimestampUtc: dayjs("2024-08-17T19:00:00Z"),
        originSic: {
          id: 1,
          code: "EWR",
          ownership: "C",
          lhRegionId: 5,
          timeZone: "America/New_York",
        },
        destinationSic: {
          id: 2,
          code: "ATL",
          ownership: "C",
          lhRegionId: 3,
          timeZone: "America/New_York",
        },
        currentSic: {
          id: 2,
          code: "ATL",
          ownership: "C",
          lhRegionId: 3,
          timeZone: "America/New_York",
        },
        finalDestination: "ATL",
        pts: 1,
        pcs: 2,
        weight: 1452,
        shipmentTags: ["CC", "UC", "APTM"],
        status: "ENRT",
        tripNumber: 756394,
        etaUtc: dayjs("2024-08-16T19:00:00Z"),
        ataUtc: dayjs("2024-08-16T20:15:00Z"),
        etaStatus: "LATE",
        locationDetails: {
          state: "TN",
          city: "Mount Pleasant",
          zipCode: "38647",
          locationSic: "ATL",
        },
        consignee: {
          id: 87520,
          name: "CAPE MARINE INC",
          address: "1427 N Main ST",
          city: "Mount Pleasant",
          state: "TN",
          zipCode: "38647",
        },
        noDimensions: false,
        serviceFailure: true,
        expediteShipment: true,
        hasProWarning: true,
        arrivalToSic: "8/16/24 1500",
        arrivalToSicStatus: "LATE",
        appt: "8/17/24 1500",
        fbFollowUpStatus: "PENDING APPT",
        currentZone: {
          id: 2,
          code: "ATL",
          ownership: "C",
          lhRegionId: 3,
          timeZone: "New Jersey",
        },
      },
    ] satisfies UcccOfdShipmentRow[],
  },
} satisfies Record<string, UcccOfdShipmentsListResponse>;

/** Dashboard-summary API payload (MSW + tests). */
export const mockUcccOfdDashboardSummary = {
  default: {
    totalUcCcShipments: 23,
    serviceFailureCount: 19,
    deliveredCount: 3,
    onTimePerformance: {
      percentage: 97.4,
      onTimeCount: 22,
      total: 23,
    },
    followUpStatuses: {
      delivered: 3,
      expedite: 4,
      ofd: 3,
      osd: 0,
      pendingAppt: 3,
      pendingPod: 2,
      serviceFailure: 0,
      noStatus: 5,
    },
  },
} as const satisfies Record<string, UcccOfdDashboardSummaryData>;

export type DimItem = {
  lengthIn: number;
  widthIn: number;
  heightIn: number;
};

type UcccOfdShipmentDetailsMock = {
  shipment: UcccOfdShipmentInformation;
  dimensions: DimItem[];
};

const mockUcccOfdShipmentDetailsDimensions: DimItem[] = [
  { lengthIn: 30, widthIn: 45, heightIn: 40 },
  { lengthIn: 22, widthIn: 36, heightIn: 50 },
  { lengthIn: 39, widthIn: 42, heightIn: 47 },
  { lengthIn: 30, widthIn: 45, heightIn: 40 },
  { lengthIn: 30, widthIn: 45, heightIn: 40 },
  { lengthIn: 30, widthIn: 45, heightIn: 40 },
  { lengthIn: 39, widthIn: 42, heightIn: 50 },
  { lengthIn: 30, widthIn: 45, heightIn: 40 },
  { lengthIn: 30, widthIn: 45, heightIn: 40 },
];

export const mockUcccOfdShipmentDetails = {
  default: {
    shipment: {
      proNumber: "180403917",
      status: "ENRT",
      statusStartDateUtc: dayjs("2026-02-02T08:19:10.211Z").toDate(),
      shipmentTags: ["UC", "CC", "RESI", "APTM"],
      originSic: {
        code: "CHT",
        zipCode: "28206",
        timeZone: "America/New_York",
      },
      destinationSic: {
        code: "ONT",
        zipCode: "92337",
        timeZone: "America/Los_Angeles",
      },
      serviceDue: dayjs("2026-02-06T00:00:00").toDate(),
      appointmentStartTimeUtc: null,
      appointmentEndTimeUtc: null,
      pallets: 1,
      pieces: 0,
      weight: 287,
      shippingInfo: {
        shipper: {
          name: "SPROGS",
          address: "716 N SECREST AVE",
          city: "MONROE",
          state: "NC",
          zipCode: "28110",
          phoneNumber: "",
        },
        consignee: {
          name: "MARGUERITTE MARSHALL",
          address: "80455 CAMINO SAN LUCAS",
          city: "INDIO",
          state: "CA",
          zipCode: "92203",
          phoneNumber: "425 985 4088",
        },
      },
      descriptionOfArticles: {
        items: [
          {
            commodityCode: "79300-3",
            description: "LOCKERS CL250",
            rateClass: "125",
            actualClass: "250",
          },
        ],
        instructions: [
          "Do Not Break Down Pallet",
          "Do Not Double Stack",
          "Fragile - Handle With Care",
        ],
      },
      palletDimensions: [
        { refDimId: 1, length: 48, width: 40, height: 60 },
        { refDimId: 2, length: 50, width: 42, height: 55 },
        { refDimId: 3, length: 45, width: 38, height: 58 },
        { refDimId: 4, length: 52, width: 44, height: 62 },
        { refDimId: 5, length: 47, width: 39, height: 57 },
      ],
      fbFollowUpStatusDetails: {
        isExpedite: true,
        isServiceFailure: true,
        trackingDetails: {
          expedite: {
            note: "Test-02",
            amount: 30,
            carrierCode: "L004",
            carrierName: "COVENANT TRANSPORT INC",
            employeeCode: null,
            employeeName: null,
            expediteCreatedOn: dayjs("2026-03-25T11:17:31.5775086Z"),
            expediteCreatedBy: "7edc4377-40a9-4348-9be4-47fc1cfbde4a",
            expediteCreatorName: "Praveen Donga",
            expediteModifiedOn: dayjs("2026-03-25T11:25:16.0564507Z"),
            expediteModifiedBy: "7edc4377-40a9-4348-9be4-47fc1cfbde4a",
            expediteModifierName: "Praveen Donga",
          },
          serviceFailure: {
            failureReasonCode: "AGENT FAILURE",
            subReason: "LATE OB CLOSE",
            explanation: "[Agent] [Origin] terminal overflowed the shipment",
            serviceFailureCreatedOn: dayjs("2026-03-18T15:30:00.6868273Z"),
            serviceFailureCreatedBy: "13a04900-fb21-41c5-b9cc-65b083a53c62",
            serviceFailureCreatorName: "Keerthi Majji",
            serviceFailureModifiedOn: dayjs("2026-03-23T18:19:25.8149345Z"),
            serviceFailureModifiedBy: "7edc4377-40a9-4348-9be4-47fc1cfbde4a",
            serviceFailureModifierName: "Praveen Donga",
          },
        },
        fbFollowUpStatus: "EXPEDITE",
        createdOn: dayjs("2026-03-18T12:08:37.515281Z"),
        createdBy: "c4cf2858-b7f2-44ed-b253-2e0bf6f8b9bd",
        modifiedOn: dayjs("2026-03-25T11:25:16.05645Z"),
        modifiedBy: "7edc4377-40a9-4348-9be4-47fc1cfbde4a",
      },
    },
    dimensions: mockUcccOfdShipmentDetailsDimensions,
  },
} satisfies Record<string, UcccOfdShipmentDetailsMock>;

export const mockServiceFailureReasonCodes: ServiceFailureReasonCode[] = [
  {
    id: 1,
    failureReasonCode: "AGENT FAILURE",
    subReasons: [
      {
        id: 1,
        subReason: "LATE OB CLOSE",
        explanation: "AGENT CUT THE LOAD OUT LATE",
      },
      {
        id: 2,
        subReason: "OVERFLOW",
        explanation: "ORIGIN TERMINAL OVERFLOWED THE SHIPMENT",
      },
      {
        id: 3,
        subReason: "OVERSIZE",
        explanation:
          "OVERSIZED SHIPMENT WAS TOO LARGE AND HAD TO BE HELD AT ORIGIN RESULTING IN DELAY ",
      },
      {
        id: 4,
        subReason: "CAPACITY",
        explanation:
          "DELAYED DUE TO [Agent] ERROR (LACK OF MANPOWER, FREIGHT DELIVERED TO WRONG CONSIGNEE, ETC.)",
      },
      {
        id: 5,
        subReason: "LATE DELIVERY",
        explanation:
          "DELIVERY DRIVER ARRIVED LATE FOR DELIVERY (AFTER 5PM FOR UC OR AFTER SPECIFIED CC WINDOW)",
      },
      {
        id: 6,
        subReason: "MISSED ORIGIN HUB TRANSFER",
        explanation:
          "[Origin] SERVICE CENTER FAILED TO TRANSFER SHIPMENT TO OB LOADING HUB",
      },
      {
        id: 7,
        subReason: "DEST HELD",
        explanation: "[Destination] TERMINAL HELD SHIPMENT IN ERROR",
      },
      {
        id: 8,
        subReason: "DELIVERED CC EARLY",
        explanation: "DELIVERED THE CC SHIPMENT BEFORE SPECIFIED TIME WINDOW",
      },
      {
        id: 9,
        subReason: "OTHER",
        explanation: "REASONS OTHER THAN WHAT IS CURRENTLY LISTED",
      },
    ],
  },
  {
    id: 2,
    failureReasonCode: "DAYLIGHT FAILURE",
    subReasons: [
      {
        id: 10,
        subReason: "LATE OB CLOSE",
        explanation: "[Origin] SERVICE CENTER CUT THE LOAD OUT LATE",
      },
      {
        id: 11,
        subReason: "OVERFLOW",
        explanation: "[Origin] SERVICE CENTER OVERFLOWED THE SHIPMENT",
      },
      {
        id: 12,
        subReason: "OVERSIZE",
        explanation:
          "OVERSIZED SHIPMENT WAS TOO LARGE AND HAD TO BE HELD AT ORIGIN RESULTING IN DELAY ",
      },
      {
        id: 13,
        subReason: "DEST HELD",
        explanation: "DESTINATION TERMINAL HELD SHIPMENT IN ERROR",
      },
      {
        id: 14,
        subReason: "DRIVER PERFORMANCE",
        explanation: "DELAYED DUE TO DAYLIGHT DRIVER PERFORMANCE RELATED ISSUE",
      },
      {
        id: 15,
        subReason: "MECH",
        explanation:
          "DRIVER HAS A MECHANICAL DELAY (FLAT TIRE, WINDSHIELD REPAIRS, ENGINE FAILURE, ETC.)",
      },
      {
        id: 16,
        subReason: "LATE DELIVERY",
        explanation:
          "DELIVERY DRIVER ARRIVED LATE FOR DELIVERY (MISSED GUARANTEED DELIVERY WINDOW, CC DELIVERY BEFORE 9 AM, ETC.)",
      },
      {
        id: 17,
        subReason: "DELIVERED CC EARLY",
        explanation: "DELIVERED THE CC SHIPMENT BEFORE SPECIFIED TIME WINDOW",
      },
      {
        id: 18,
        subReason: "SVC STANDARD REVIEW",
        explanation: "DELIVERY UNDER REVIEW WITH DIRECTOR",
      },
      {
        id: 19,
        subReason: "SERVICE CENTER ERROR",
        explanation: "SERVICE CENTER RELATED ERROR",
      },
      {
        id: 20,
        subReason: "LINEHAUL ERROR",
        explanation: "LINEHAUL TEAM RELATED ERROR",
      },
      {
        id: 21,
        subReason: "BILLING ERROR",
        explanation:
          "NOT BILLED IN TIME OR BILLED TOO LATE CAUSING LOSS OF VISIBILITY",
      },
      {
        id: 22,
        subReason: "CS ERROR",
        explanation: "CLIENT SERVICES TEAM RELATED ERROR",
      },
      {
        id: 23,
        subReason: "OTHER",
        explanation: "REASONS OTHER THAN WHAT IS CURRENTLY LISTED",
      },
    ],
  },
  {
    id: 3,
    failureReasonCode: "CARRIER FAILURE",
    subReasons: [
      {
        id: 24,
        subReason: "LATE PICKUP",
        explanation: "[Carrier] PICKED UP LOAD LATE FROM [Origin]",
      },
      {
        id: 25,
        subReason: "MECH",
        explanation:
          "[Carrier] HAS A MECHANICAL DELAY (FLAT TIRE, WINDSHIELD REPAIRS, ENGINE FAILURE, ETC.)",
      },
      {
        id: 26,
        subReason: "DRIVER PERFORMANCE",
        explanation:
          "DELAYED DUE TO DRIVER PERFORMANCE RELATED ISSUE (FATIGUE, ILLNESS, DRIVER GOT OFF TRUCK)",
      },
      {
        id: 27,
        subReason: "CARRIER PERFORMANCE",
        explanation:
          "DELAYED DUE TO [Carrier] RELATED ISSUE (LATE REPOWER, DRIVER HOME TIME, BOOKED LOAD WITH WRONG DELIVERY INFO, ETC.)",
      },
      {
        id: 28,
        subReason: "OTHER",
        explanation: "REASONS OTHER THAN WHAT IS CURRENTLY LISTED",
      },
    ],
  },
];
